import { useState, useEffect, useCallback, useRef } from "react";
import {
  X, Send, Paperclip, MessageCircle, ThumbsUp, MessageSquare,
  Trash2, Download, File, FileText, FileSpreadsheet, ImageIcon,
  Building2, Calendar, Hash, StickyNote, Clock,
  Package, CheckCircle, AlertCircle, MapPin, User,
  Wrench, Tag, BarChart2, ClipboardList
} from "lucide-react";
import API_BASE_URL from "../config";

// ─── CONSTANTS ────────────────────────────────────────────────
const ALLOWED_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const REACTIONS = [
  { emoji: "👍", label: "Like",  color: "#1D4ED8", bg: "#EFF6FF" },
  { emoji: "❤️", label: "Love",  color: "#EF4444", bg: "#FEF2F2" },
  { emoji: "😂", label: "Haha",  color: "#F59E0B", bg: "#FFFBEB" },
  { emoji: "😮", label: "Wow",   color: "#3B82F6", bg: "#EFF6FF" },
  { emoji: "😢", label: "Sad",   color: "#6B7280", bg: "#F9FAFB" },
  { emoji: "😡", label: "Angry", color: "#EF4444", bg: "#FEF2F2" },
];

const AVATAR_COLORS = [
  { from: "#1E40AF", to: "#2563EB" },
  { from: "#1D4ED8", to: "#3B82F6" },
  { from: "#0369A1", to: "#2563EB" },
  { from: "#1E40AF", to: "#0EA5E9" },
  { from: "#2563EB", to: "#60A5FA" },
  { from: "#1D4ED8", to: "#38BDF8" },
  { from: "#0284C7", to: "#3B82F6" },
];

const MR_STATUS_MAP = {
  "-1": { label: "Rejected",  dot: "#EF4444", color: "#991B1B", bg: "#FEF2F2" },
  "0":  { label: "Draft",     dot: "#94A3B8", color: "#64748B", bg: "#F8FAFC" },
  "1":  { label: "Pending",   dot: "#F59E0B", color: "#92400E", bg: "#FFFBEB" },
  "2":  { label: "Approved",  dot: "#10B981", color: "#065F46", bg: "#ECFDF5" },
  "3":  { label: "Issued",    dot: "#2563EB", color: "#1E40AF", bg: "#EFF6FF" },
  "4":  { label: "Cancelled", dot: "#6B7280", color: "#374151", bg: "#F9FAFB" },
};

// ─── PROFILE CACHE ────────────────────────────────────────────
const PROFILE_CACHE_KEY = "afm_mr_profileCache_v1";
const CACHE_EXPIRY_MS   = 60 * 60 * 1000;

const getProfileCache = () => {
  try { return JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || "{}"); }
  catch { return {}; }
};
const setProfileCache = (name, url) => {
  try {
    const c = getProfileCache();
    c[name] = { url, ts: Date.now() };
    localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(c));
  } catch {}
};
const getProfileFromCache = (name) => {
  try {
    const e = getProfileCache()[name];
    if (!e || Date.now() - e.ts > CACHE_EXPIRY_MS) return null;
    return e.url;
  } catch { return null; }
};

// ─── HELPERS ──────────────────────────────────────────────────
const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024)        return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTimeAgo = (dateString) => {
  const diff = (new Date() - new Date(dateString)) / 1000;
  if (diff < 60)     return "Just now";
  if (diff < 3600)   return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)  return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const getFileIcon = (type) => {
  if (!type) return <File size={14} />;
  if (type.startsWith("image/"))  return <ImageIcon size={14} />;
  if (type === "application/pdf") return <FileText size={14} />;
  if (type.includes("word"))      return <FileText size={14} />;
  if (type.includes("sheet"))     return <FileSpreadsheet size={14} />;
  return <File size={14} />;
};

const getAvatarColor = (name) =>
  AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

// ─── STYLES ───────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .afm-mr-root { font-family: 'DM Sans', sans-serif; }
  .afm-mr-mono { font-family: 'DM Mono', monospace; }

  .afm-mr-backdrop { animation: afmMrFadeIn 0.2s ease; }
  .afm-mr-modal    { animation: afmMrSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }

  @keyframes afmMrFadeIn  { from { opacity:0 } to { opacity:1 } }
  @keyframes afmMrSlideUp {
    from { opacity:0; transform: translateY(24px) scale(0.97); }
    to   { opacity:1; transform: translateY(0) scale(1); }
  }

  .afm-mr-feed-item { animation: afmMrFeedIn 0.35s ease both; }
  @keyframes afmMrFeedIn {
    from { opacity:0; transform:translateY(10px); }
    to   { opacity:1; transform:translateY(0); }
  }

  .afm-mr-reaction-picker {
    transition: opacity 0.15s ease, transform 0.2s cubic-bezier(.34,1.56,.64,1);
  }
  .afm-mr-reaction-btn { transition: transform 0.15s cubic-bezier(.34,1.56,.64,1); }
  .afm-mr-reaction-btn:hover { transform: scale(1.5) translateY(-4px); }

  .afm-mr-float {
    position:fixed; font-size:1.5rem; pointer-events:none;
    animation: afmMrFloat 0.7s ease forwards; z-index:9999;
  }
  @keyframes afmMrFloat {
    from { opacity:1; transform:translateY(0) scale(1); }
    to   { opacity:0; transform:translateY(-60px) scale(1.5); }
  }

  @keyframes afmMrLikePop {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.4); }
    100% { transform: scale(1); }
  }
  .afm-mr-like-pop { animation: afmMrLikePop 0.3s cubic-bezier(.34,1.56,.64,1); }

  .afm-mr-post-box { transition: box-shadow 0.2s ease, border-color 0.2s ease; }
  .afm-mr-post-box:focus-within {
    box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
    border-color: #2563EB !important;
  }

  .afm-mr-comment-input:focus {
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
    border-color: #2563EB !important;
    outline: none;
  }

  .afm-mr-scrollbar::-webkit-scrollbar { width:4px; }
  .afm-mr-scrollbar::-webkit-scrollbar-track { background:transparent; }
  .afm-mr-scrollbar::-webkit-scrollbar-thumb { background:#BFDBFE; border-radius:99px; }
  .afm-mr-scrollbar::-webkit-scrollbar-thumb:hover { background:#93C5FD; }

  .afm-mr-shimmer {
    background: linear-gradient(90deg,#EFF6FF 25%,#DBEAFE 50%,#EFF6FF 75%);
    background-size: 200% 100%;
    animation: afmMrShimmer 1.4s infinite;
  }
  @keyframes afmMrShimmer {
    from { background-position:200% 0; }
    to   { background-position:-200% 0; }
  }

  .afm-mr-comment-row:hover .afm-mr-del-comment { opacity: 1 !important; }

  @keyframes afmMrSpin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;

// ─── SPAWN FLOAT EMOJI ────────────────────────────────────────
const spawnFloat = (emoji, x, y) => {
  const el = document.createElement("span");
  el.className = "afm-mr-float";
  el.textContent = emoji;
  el.style.left = (x - 16) + "px";
  el.style.top  = (y - 16) + "px";
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 700);
};

// ─── AVATAR ───────────────────────────────────────────────────
const UserAvatar = ({ name, size = "md", userProfiles }) => {
  const [imgError, setImgError] = useState(false);
  const initial = (name || "?")[0].toUpperCase();
  const { from, to } = getAvatarColor(name);
  const sz = size === "sm" ? 28 : size === "xs" ? 22 : 38;
  const fs = size === "sm" ? "0.65rem" : size === "xs" ? "0.55rem" : "0.85rem";

  const profile = name ? userProfiles[name] : undefined;
  const resolvedUrl = (() => {
    const url = profile?.profilePictureUrl || profile?.avatarUrl || profile?.photo;
    if (!url) return null;
    return url.startsWith("http") ? url : `${API_BASE_URL}${url}`;
  })();

  const style = {
    width: sz, height: sz, borderRadius: "50%", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: fs, fontWeight: 700, color: "white",
    background: `linear-gradient(135deg,${from},${to})`,
    boxShadow: "0 0 0 2px white, 0 0 0 3px rgba(37,99,235,0.2)",
  };

  if (resolvedUrl && !imgError) {
    return (
      <img src={resolvedUrl} alt={name || "User"}
        style={{ ...style, objectFit: "cover", background: "none" }}
        onError={() => setImgError(true)}
      />
    );
  }
  return <div style={style}>{initial}</div>;
};

// ─── REACTION PICKER ──────────────────────────────────────────
const ReactionPicker = ({ feedId, myReaction, onReact }) => {
  const [open, setOpen]       = useState(false);
  const [hovered, setHovered] = useState(null);
  const timer  = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const myData = REACTIONS.find(r => r.emoji === myReaction?.reactionType);

  const handleReact = (emoji) => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      spawnFloat(emoji, rect.left + rect.width / 2, rect.top);
      btnRef.current.classList.remove("afm-mr-like-pop");
      void btnRef.current.offsetWidth;
      btnRef.current.classList.add("afm-mr-like-pop");
    }
    onReact(feedId, emoji);
    setOpen(false);
  };

  const handleLikeClick = () => handleReact(myData ? myData.emoji : "👍");

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(true), 280); }}
      onMouseLeave={() => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 350); }}
    >
      <div className="afm-mr-reaction-picker" style={{
        position: "absolute", bottom: "calc(100% + 6px)", left: 0, zIndex: 200,
        display: "flex", alignItems: "center", gap: 2,
        background: "white", borderRadius: 999, padding: "6px 8px",
        boxShadow: "0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #DBEAFE",
        opacity: open ? 1 : 0,
        transform: open ? "scale(1) translateY(0)" : "scale(0.6) translateY(8px)",
        pointerEvents: open ? "auto" : "none",
        transformOrigin: "bottom left",
        whiteSpace: "nowrap",
      }}>
        {REACTIONS.map(r => (
          <button key={r.emoji} className="afm-mr-reaction-btn"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
                     display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}
            onClick={() => handleReact(r.emoji)}
            onMouseEnter={() => setHovered(r.emoji)}
            onMouseLeave={() => setHovered(null)}
          >
            <span style={{ fontSize: "1.6rem", lineHeight: 1, fontFamily: "'Noto Color Emoji',sans-serif" }}>
              {r.emoji}
            </span>
            {hovered === r.emoji && (
              <span style={{ fontSize: "0.55rem", fontWeight: 700, color: "#2563EB",
                             background: "#DBEAFE", borderRadius: 99, padding: "1px 5px" }}>
                {r.label}
              </span>
            )}
          </button>
        ))}
      </div>

      <button ref={btnRef} onClick={handleLikeClick}
        style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px",
                 borderRadius: 8, border: "none", background: "none", cursor: "pointer",
                 fontSize: "0.78rem", fontWeight: 600, fontFamily: "inherit",
                 color: myData ? myData.color : "#64748B", transition: "background 0.15s ease" }}
        onMouseEnter={e => e.currentTarget.style.background = "#EFF6FF"}
        onMouseLeave={e => e.currentTarget.style.background = "none"}
      >
        {myData ? <span style={{ fontSize: "1rem", lineHeight: 1 }}>{myData.emoji}</span> : <ThumbsUp size={14} />}
        {myData ? myData.label : "Like"}
      </button>
    </div>
  );
};

// ─── SKELETON ─────────────────────────────────────────────────
const FeedSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {[1, 2, 3].map(i => (
      <div key={i} style={{ background: "white", borderRadius: 16, padding: 20,
                             border: "1px solid #DBEAFE", overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
          <div className="afm-mr-shimmer" style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <div className="afm-mr-shimmer" style={{ height: 12, width: "40%", borderRadius: 6 }} />
            <div className="afm-mr-shimmer" style={{ height: 10, width: "25%", borderRadius: 6 }} />
          </div>
        </div>
        <div className="afm-mr-shimmer" style={{ height: 10, width: "80%", borderRadius: 6, marginBottom: 6 }} />
        <div className="afm-mr-shimmer" style={{ height: 10, width: "60%", borderRadius: 6 }} />
      </div>
    ))}
  </div>
);

// ─── INFO ROW ─────────────────────────────────────────────────
const InfoRow = ({ label, value, icon: Icon }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, padding: "10px 14px",
                  background: "white", borderRadius: 12, border: "1px solid #DBEAFE" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.65rem",
                    fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "#93C5FD" }}>
        {Icon && <Icon size={10} />} {label}
      </div>
      <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#1E293B", wordBreak: "break-word" }}>
        {value}
      </div>
    </div>
  );
};

// ─── SPINNER ──────────────────────────────────────────────────
const Spinner = () => (
  <span style={{
    width: 12, height: 12, borderRadius: "50%",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid white",
    display: "inline-block",
    animation: "afmMrSpin 0.8s linear infinite",
  }} />
);

// ─── MAIN COMPONENT ───────────────────────────────────────────
export default function ActivityFeedModal_ItemRequest({ mr, onClose }) {
  const [feeds,             setFeeds]             = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [newPost,           setNewPost]           = useState("");
  const [posting,           setPosting]           = useState(false);
  const [commentInputs,     setCommentInputs]     = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [selectedFile,      setSelectedFile]      = useState(null);
  const [uploading,         setUploading]         = useState(false);
  const [isDragging,        setIsDragging]        = useState(false);
  const [userProfiles,      setUserProfiles]      = useState({});
  const [error,             setError]             = useState(null);

  const fileInputRef  = useRef(null);
  const feedEndRef    = useRef(null);
  const fetchedNames  = useRef(new Set());
  const textareaRef   = useRef(null);

  const userRef       = useRef(JSON.parse(localStorage.getItem("user") || "{}"));
  const currentUserId = parseInt(userRef.current.userId);
  const currentName   = userRef.current.fullName || userRef.current.username || "You";

  const mrHeaderId = mr.id;
  const mrStatus   = MR_STATUS_MAP[String(mr.status)] || MR_STATUS_MAP["0"];

  // ── Fetch feeds ──────────────────────────────────────────────
  const fetchFeeds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}api/ActivityFeed/mr/${mrHeaderId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFeeds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("fetchFeeds error:", err);
      setError("Failed to load activity feed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [mrHeaderId]);

  useEffect(() => { fetchFeeds(); }, [fetchFeeds]);

  // ── Fetch avatars ────────────────────────────────────────────
  useEffect(() => {
    const names = [...new Set([
      currentName,
      ...feeds.flatMap(f => [
        f.createdByName,
        ...(f.comments || []).map(c => c.createdByName),
      ]),
    ].filter(Boolean))];

    names.forEach(name => {
      if (fetchedNames.current.has(name)) return;
      fetchedNames.current.add(name);
      const cached = getProfileFromCache(name);
      const url    = cached || `${API_BASE_URL}api/Users/username/${encodeURIComponent(name)}/profile`;
      if (!cached) setProfileCache(name, url);
      setUserProfiles(p => ({ ...p, [name]: { profilePictureUrl: url } }));
    });
  }, [feeds]);

  // ── File validation ──────────────────────────────────────────
  const validateAndSetFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      alert("File type not allowed.\nAllowed: Images, PDF, DOCX, XLSX");
      return;
    }
    if (file.size > 10 * 1024 * 1024) { alert("File too large. Max 10MB."); return; }
    setSelectedFile(file);
  };

  // ── Post ─────────────────────────────────────────────────────
  const handlePost = async () => {
    if (!newPost.trim() && !selectedFile) return;
    setPosting(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/ActivityFeed`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mrHeaderId,
          prHeaderId:    null,
          poHeaderId:    null,
          quotationId:   null,
          fuelHeaderId:  null,
          content:       newPost.trim() || `Uploaded: ${selectedFile?.name}`,
          feedType:      selectedFile ? "file" : "text",
          createdBy:     currentUserId,
          createdByName: currentName,
        }),
      });

      if (!res.ok) {
        const errText = await res.text();
        alert(`Failed to create post (${res.status}):\n${errText}`);
        return;
      }

      const created = await res.json();

      if (selectedFile && created?.id) {
        setUploading(true);
        const fd = new FormData();
        fd.append("file", selectedFile);
        const uploadRes = await fetch(
          `${API_BASE_URL}api/ActivityFeed/${created.id}/upload`,
          { method: "POST", body: fd }
        );
        if (!uploadRes.ok) console.error("Upload failed:", await uploadRes.text());
        setUploading(false);
      }

      setNewPost("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchFeeds();
      setTimeout(() => feedEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      console.error("handlePost error:", err);
      alert("Error: " + err.message);
    } finally {
      setPosting(false);
      setUploading(false);
    }
  };

  // ── Comment ──────────────────────────────────────────────────
  const handleComment = async (feedId) => {
    const text = commentInputs[feedId];
    if (!text?.trim()) return;
    setSubmittingComment(p => ({ ...p, [feedId]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}api/ActivityFeed/${feedId}/comment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment: text.trim(), createdBy: currentUserId, createdByName: currentName }),
      });
      if (!res.ok) { alert(`Comment failed: ${await res.text()}`); return; }
      setCommentInputs(p => ({ ...p, [feedId]: "" }));
      await fetchFeeds();
    } catch (err) { console.error(err); }
    finally { setSubmittingComment(p => ({ ...p, [feedId]: false })); }
  };

  // ── React ─────────────────────────────────────────────────────
  const handleReact = async (feedId, reactionType) => {
    try {
      const res = await fetch(`${API_BASE_URL}api/ActivityFeed/${feedId}/react`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: currentUserId, userName: currentName, reactionType }),
      });
      if (!res.ok) { console.error("React failed:", await res.text()); return; }
      await fetchFeeds();
    } catch (err) { console.error(err); }
  };

  // ── Delete ────────────────────────────────────────────────────
  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm("Delete this post?")) return;
    await fetch(`${API_BASE_URL}api/ActivityFeed/${feedId}`, { method: "DELETE" });
    await fetchFeeds();
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    await fetch(`${API_BASE_URL}api/ActivityFeed/comment/${commentId}`, { method: "DELETE" });
    await fetchFeeds();
  };

  const totalReactions = feeds.reduce((a, f) => a + (f.reactions?.length || 0), 0);
  const totalComments  = feeds.reduce((a, f) => a + (f.comments?.length  || 0), 0);

  // ─── RENDER ──────────────────────────────────────────────────
  return (
    <>
      <style>{STYLES}</style>

      {/* Backdrop */}
      <div
        className="afm-mr-backdrop"
        style={{
          position: "fixed", inset: 0, zIndex: 200,
          background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        {/* Modal */}
        <div
          className="afm-mr-root afm-mr-modal"
          style={{
            width: "100%", maxWidth: 920, height: "min(88vh,720px)",
            background: "#F0F9FF", borderRadius: 24, overflow: "hidden",
            boxShadow: "0 32px 96px rgba(15,23,42,0.18), 0 0 0 1px rgba(37,99,235,0.08)",
            display: "flex", flexDirection: "column",
          }}
        >
          {/* ── HEADER ─────────────────────────────────────── */}
          <div style={{
            padding: "18px 24px",
            background: "linear-gradient(135deg,#1E40AF,#1D4ED8,#2563EB)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexShrink: 0, position: "relative", overflow: "hidden",
          }}>
            <div style={{ position: "absolute", top: -20, right: 80, width: 100, height: 100,
                          background: "rgba(255,255,255,0.06)", borderRadius: "50%", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: -30, right: 20, width: 80, height: 80,
                          background: "rgba(255,255,255,0.04)", borderRadius: "50%", pointerEvents: "none" }} />

            <div style={{ display: "flex", alignItems: "center", gap: 14, zIndex: 1 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 12,
                background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)", flexShrink: 0,
              }}>
                <ClipboardList size={20} color="white" />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 3 }}>
                  <h2 style={{ margin: 0, fontWeight: 700, fontSize: "1rem", color: "white", letterSpacing: "-0.01em" }}>
                    Activity Feed
                  </h2>
                  <span style={{
                    background: "rgba(255,255,255,0.2)", color: "white",
                    fontSize: "0.65rem", fontWeight: 700, padding: "2px 8px",
                    borderRadius: 99, letterSpacing: "0.04em",
                  }}>
                    {feeds.length} POSTS
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span className="afm-mr-mono" style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.75rem" }}>
                    {mr.mrNo || mr.controlNo || `MR #${mrHeaderId}`}
                  </span>
                  {mr.requestedByName && (
                    <>
                      <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "0.65rem" }}>·</span>
                      <span style={{ color: "rgba(255,255,255,0.65)", fontSize: "0.75rem" }}>{mr.requestedByName}</span>
                    </>
                  )}
                  <span style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: "rgba(255,255,255,0.15)", color: "white",
                    fontSize: "0.65rem", fontWeight: 600, padding: "2px 8px", borderRadius: 99,
                  }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: mrStatus.dot, flexShrink: 0 }} />
                    {mrStatus.label}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, zIndex: 1 }}>
              {[{ icon: "💬", val: totalComments }, { icon: "👍", val: totalReactions }].map((s, i) => (
                <div key={i} style={{
                  display: "flex", flexDirection: "column", alignItems: "center",
                  background: "rgba(255,255,255,0.12)", borderRadius: 10, padding: "6px 12px",
                }}>
                  <span style={{ fontSize: "1rem", lineHeight: 1 }}>{s.icon}</span>
                  <span style={{ color: "white", fontWeight: 700, fontSize: "0.7rem" }}>{s.val}</span>
                </div>
              ))}
              <button onClick={onClose} style={{
                width: 34, height: 34, borderRadius: 10,
                background: "rgba(255,255,255,0.12)", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "rgba(255,255,255,0.8)", marginLeft: 4, transition: "background 0.15s",
              }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* ── BODY ───────────────────────────────────────── */}
          <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>

            {/* LEFT: feed column */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>

              {/* Composer */}
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #BFDBFE",
                            background: "white", flexShrink: 0 }}>
                <div
                  className="afm-mr-post-box"
                  style={{
                    border: "1.5px solid #BFDBFE", borderRadius: 16, overflow: "hidden",
                    background: "#EFF6FF",
                    ...(isDragging ? {
                      background: "linear-gradient(135deg,#DBEAFE,#EFF6FF)",
                      borderColor: "#2563EB", borderStyle: "dashed",
                    } : {}),
                  }}
                  onDragOver={e  => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={e => {
                    e.preventDefault(); setIsDragging(false);
                    const f = e.dataTransfer.files[0]; if (f) validateAndSetFile(f);
                  }}
                >
                  {isDragging ? (
                    <div style={{ padding: "28px 20px", textAlign: "center" }}>
                      <div style={{ fontSize: "2rem", marginBottom: 8 }}>📎</div>
                      <p style={{ margin: 0, color: "#2563EB", fontWeight: 600, fontSize: "0.85rem" }}>
                        Drop your file here
                      </p>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 12, padding: "14px 16px 10px" }}>
                        <UserAvatar name={currentName} size="md" userProfiles={userProfiles} />
                        <textarea ref={textareaRef} rows={2}
                          placeholder={`Share an update on ${mr.mrNo || mr.controlNo || "this MR"}...`}
                          value={newPost}
                          onChange={e => setNewPost(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handlePost(); }}
                          style={{
                            flex: 1, border: "none", background: "transparent", resize: "none",
                            fontFamily: "inherit", fontSize: "0.875rem", color: "#1E293B",
                            outline: "none", lineHeight: 1.6, padding: 0,
                          }}
                        />
                      </div>

                      {selectedFile && (
                        <div style={{
                          margin: "0 16px 10px", display: "flex", alignItems: "center", gap: 10,
                          background: "#DBEAFE", borderRadius: 10, padding: "8px 12px",
                          border: "1px solid #BFDBFE",
                        }}>
                          <span style={{ color: "#2563EB" }}>{getFileIcon(selectedFile.type)}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#1E40AF",
                                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {selectedFile.name}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "#60A5FA" }}>
                              {formatFileSize(selectedFile.size)}
                            </div>
                          </div>
                          <button
                            onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                            style={{ background: "none", border: "none", cursor: "pointer",
                                     color: "#93C5FD", padding: 4, borderRadius: 6, display: "flex" }}
                            onMouseEnter={e => e.currentTarget.style.color = "#EF4444"}
                            onMouseLeave={e => e.currentTarget.style.color = "#93C5FD"}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      )}

                      <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "8px 16px 12px", borderTop: "1px solid #DBEAFE",
                      }}>
                        <button onClick={() => fileInputRef.current?.click()} style={{
                          display: "flex", alignItems: "center", gap: 6,
                          background: "none", border: "1px solid #BFDBFE", borderRadius: 8,
                          padding: "5px 12px", cursor: "pointer", color: "#64748B",
                          fontSize: "0.75rem", fontWeight: 600, fontFamily: "inherit", transition: "all 0.15s",
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = "#2563EB"; e.currentTarget.style.color = "#2563EB"; e.currentTarget.style.background = "#DBEAFE"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "#BFDBFE"; e.currentTarget.style.color = "#64748B"; e.currentTarget.style.background = "none"; }}
                        >
                          <Paperclip size={12} /> Attach file
                        </button>
                        <input ref={fileInputRef} type="file" accept="image/*,.pdf,.docx,.xlsx"
                          style={{ display: "none" }}
                          onChange={e => { const f = e.target.files[0]; if (f) validateAndSetFile(f); }}
                        />
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: "0.7rem", color: "#CBD5E1" }}>Ctrl+Enter</span>
                          <button onClick={handlePost}
                            disabled={posting || uploading || (!newPost.trim() && !selectedFile)}
                            style={{
                              display: "flex", alignItems: "center", gap: 7,
                              background: "linear-gradient(135deg,#1E40AF,#2563EB)",
                              color: "white", border: "none", borderRadius: 10,
                              padding: "8px 18px", cursor: "pointer",
                              fontSize: "0.8rem", fontWeight: 700, fontFamily: "inherit",
                              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
                              opacity: (posting || uploading || (!newPost.trim() && !selectedFile)) ? 0.45 : 1,
                            }}
                          >
                            {uploading ? <><Spinner /> Uploading</>
                              : posting  ? <><Spinner /> Posting</>
                              : <><Send size={13} /> Post</>}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Feed list */}
              <div className="afm-mr-scrollbar"
                style={{ flex: 1, overflowY: "auto", padding: "16px 20px",
                         display: "flex", flexDirection: "column", gap: 14 }}
              >
                {loading ? <FeedSkeleton /> : error ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                                justifyContent: "center", flex: 1, padding: "40px 20px" }}>
                    <AlertCircle size={32} style={{ color: "#EF4444", marginBottom: 10 }} />
                    <p style={{ margin: "0 0 12px", fontWeight: 600, fontSize: "0.9rem", color: "#EF4444" }}>{error}</p>
                    <button onClick={fetchFeeds} style={{
                      padding: "8px 18px", borderRadius: 10, border: "none", cursor: "pointer",
                      background: "linear-gradient(135deg,#1E40AF,#2563EB)", color: "white",
                      fontWeight: 700, fontSize: "0.8rem", fontFamily: "inherit",
                    }}>Try Again</button>
                  </div>
                ) : feeds.length === 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center",
                                justifyContent: "center", flex: 1, padding: "60px 20px", color: "#94A3B8" }}>
                    <div style={{ fontSize: "3rem", marginBottom: 12, opacity: 0.5 }}>💬</div>
                    <p style={{ margin: "0 0 4px", fontWeight: 600, fontSize: "0.9rem", color: "#93C5FD" }}>
                      No activity yet
                    </p>
                    <p style={{ margin: 0, fontSize: "0.8rem" }}>Be the first to post an update!</p>
                  </div>
                ) : feeds.map((feed, feedIdx) => {
                  const reactionMap = {};
                  (feed.reactions || []).forEach(r => {
                    reactionMap[r.reactionType] = (reactionMap[r.reactionType] || 0) + 1;
                  });
                  const myReaction = (feed.reactions || []).find(r => r.userId === currentUserId);
                  const isMe       = feed.createdBy === currentUserId;

                  return (
                    <div key={feed.id} className="afm-mr-feed-item"
                      style={{
                        animationDelay: `${feedIdx * 40}ms`,
                        background: "white", borderRadius: 18, overflow: "visible",
                        border: "1px solid #DBEAFE",
                        boxShadow: "0 1px 4px rgba(37,99,235,0.06)",
                        transition: "box-shadow 0.2s ease",
                      }}
                      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 20px rgba(37,99,235,0.1)"}
                      onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(37,99,235,0.06)"}
                    >
                      {/* Feed header */}
                      <div style={{ display: "flex", gap: 12, padding: "14px 16px 10px", alignItems: "flex-start" }}>
                        <UserAvatar name={feed.createdByName} size="md" userProfiles={userProfiles} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#1E293B" }}>
                                {feed.createdByName || "Unknown"}
                              </span>
                              <span style={{ fontSize: "0.72rem", color: "#94A3B8",
                                             display: "flex", alignItems: "center", gap: 3 }}>
                                <Clock size={10} /> {formatTimeAgo(feed.createdAt)}
                              </span>
                              {feed.feedType === "file" && (
                                <span style={{
                                  fontSize: "0.68rem", fontWeight: 600, color: "#2563EB",
                                  background: "#DBEAFE", borderRadius: 99, padding: "1px 8px",
                                  display: "flex", alignItems: "center", gap: 4, border: "1px solid #BFDBFE",
                                }}>
                                  <Paperclip size={9} /> with file
                                </span>
                              )}
                            </div>
                            {isMe && (
                              <button onClick={() => handleDeleteFeed(feed.id)} style={{
                                background: "none", border: "none", cursor: "pointer",
                                color: "#CBD5E1", borderRadius: 7, padding: 5, display: "flex", transition: "all 0.15s",
                              }}
                                onMouseEnter={e => { e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.background = "#FEF2F2"; }}
                                onMouseLeave={e => { e.currentTarget.style.color = "#CBD5E1"; e.currentTarget.style.background = "none"; }}
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                          <p style={{ margin: 0, fontSize: "0.875rem", color: "#374151", lineHeight: 1.65 }}>
                            {feed.content}
                          </p>
                          {((feed.reactions || []).length > 0 || (feed.comments || []).length > 0) && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8 }}>
                              {(feed.reactions || []).length > 0 && (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "#DBEAFE", borderRadius: 99, padding: "3px 10px",
                                  fontSize: "0.72rem", fontWeight: 700, color: "#1E40AF",
                                  border: "1px solid #BFDBFE",
                                }}>
                                  👍 {(feed.reactions || []).length}
                                </span>
                              )}
                              {(feed.comments || []).length > 0 && (
                                <span style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "#EFF6FF", borderRadius: 99, padding: "3px 10px",
                                  fontSize: "0.72rem", fontWeight: 700, color: "#2563EB",
                                  border: "1px solid #BFDBFE",
                                }}>
                                  💬 {(feed.comments || []).length}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Attachments */}
                      {(feed.attachments || []).length > 0 && (
                        <div style={{ padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                          {feed.attachments.map(att => {
                            const isImg = att.fileType?.startsWith("image/");
                            const url   = `${API_BASE_URL}api/ActivityFeed/attachment/file/${att.id}`;
                            return isImg ? (
                              <div key={att.id} style={{ borderRadius: 12, overflow: "hidden",
                                                          border: "1px solid #BFDBFE", background: "#EFF6FF" }}>
                                <img src={url} alt={att.fileName}
                                  style={{ width: "100%", maxHeight: 240, objectFit: "contain",
                                           cursor: "pointer", display: "block", transition: "opacity 0.15s" }}
                                  onClick={() => window.open(url, "_blank")}
                                  onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                                  onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                />
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                                              padding: "8px 12px", background: "#DBEAFE", borderTop: "1px solid #BFDBFE" }}>
                                  <div>
                                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#1E40AF",
                                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>
                                      {att.fileName}
                                    </div>
                                    <div style={{ fontSize: "0.68rem", color: "#60A5FA" }}>{formatFileSize(att.fileSize)}</div>
                                  </div>
                                  <a href={url} download={att.fileName} style={{
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    width: 28, height: 28, borderRadius: 8, background: "#BFDBFE",
                                    color: "#1E40AF", textDecoration: "none", transition: "all 0.15s",
                                  }}
                                    onMouseEnter={e => { e.currentTarget.style.background = "#2563EB"; e.currentTarget.style.color = "white"; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = "#BFDBFE"; e.currentTarget.style.color = "#1E40AF"; }}
                                  >
                                    <Download size={13} />
                                  </a>
                                </div>
                              </div>
                            ) : (
                              <a key={att.id} href={url} target="_blank" rel="noreferrer"
                                style={{
                                  display: "flex", alignItems: "center", gap: 10,
                                  background: "linear-gradient(135deg,#DBEAFE,#EFF6FF)",
                                  border: "1px solid #BFDBFE", borderRadius: 12,
                                  padding: "10px 14px", textDecoration: "none", transition: "all 0.15s",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = "#2563EB"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(37,99,235,0.15)"; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = "#BFDBFE"; e.currentTarget.style.boxShadow = "none"; }}
                              >
                                <span style={{ color: "#2563EB" }}>{getFileIcon(att.fileType)}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#1E40AF",
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {att.fileName}
                                  </div>
                                  <div style={{ fontSize: "0.7rem", color: "#60A5FA" }}>{formatFileSize(att.fileSize)}</div>
                                </div>
                                <Download size={14} style={{ color: "#93C5FD", flexShrink: 0 }} />
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {/* Action bar */}
                      <div style={{
                        padding: "6px 12px", borderTop: "2px solid #DBEAFE",
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        background: "white", borderRadius: "0 0 18px 18px",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <ReactionPicker feedId={feed.id} myReaction={myReaction} onReact={handleReact} />
                          <button style={{
                            display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                            borderRadius: 8, border: "none", background: "none", cursor: "pointer",
                            fontSize: "0.8rem", fontWeight: 700, fontFamily: "inherit",
                            color: (feed.comments || []).length > 0 ? "#2563EB" : "#64748B",
                            transition: "background 0.15s",
                          }}
                            onMouseEnter={e => e.currentTarget.style.background = "#EFF6FF"}
                            onMouseLeave={e => e.currentTarget.style.background = "none"}
                          >
                            <MessageSquare size={14} />
                            {(feed.comments || []).length > 0
                              ? `${(feed.comments || []).length} Comment${(feed.comments || []).length !== 1 ? "s" : ""}`
                              : "Comment"
                            }
                          </button>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          {Object.entries(reactionMap).map(([emoji, count]) => {
                            const r = REACTIONS.find(x => x.emoji === emoji);
                            return (
                              <span key={emoji} style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                background: r?.bg || "#F8FAFC",
                                border: `1px solid ${r?.color || "#E2E8F0"}44`,
                                borderRadius: 99, padding: "3px 10px",
                                fontSize: "0.75rem", fontWeight: 700, color: r?.color || "#64748B",
                              }}>
                                <span style={{ fontSize: "0.9rem" }}>{emoji}</span> {count}
                              </span>
                            );
                          })}
                        </div>
                      </div>

                      {/* Comments */}
                      {(feed.comments || []).length > 0 && (
                        <div style={{ background: "#EFF6FF", borderTop: "1px solid #DBEAFE",
                                      padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                          {feed.comments.map(c => (
                            <div key={c.id} className="afm-mr-comment-row"
                                 style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
                              <UserAvatar name={c.createdByName} size="sm" userProfiles={userProfiles} />
                              <div style={{ flex: 1, background: "white", borderRadius: 12,
                                            padding: "8px 12px", border: "1px solid #DBEAFE",
                                            boxShadow: "0 1px 3px rgba(37,99,235,0.06)" }}>
                                <span style={{ fontWeight: 700, fontSize: "0.75rem", color: "#374151" }}>
                                  {c.createdByName}{" "}
                                </span>
                                <span style={{ fontSize: "0.82rem", color: "#4B5563" }}>{c.comment}</span>
                                <div style={{ fontSize: "0.68rem", color: "#94A3B8", marginTop: 3 }}>
                                  {formatTimeAgo(c.createdAt)}
                                </div>
                              </div>
                              {c.createdBy === currentUserId && (
                                <button className="afm-mr-del-comment"
                                  onClick={() => handleDeleteComment(c.id)}
                                  style={{
                                    background: "none", border: "none", cursor: "pointer",
                                    color: "#E2E8F0", padding: "6px 4px", borderRadius: 6,
                                    opacity: 0, transition: "all 0.15s", marginTop: 2, display: "flex",
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.color = "#EF4444"}
                                  onMouseLeave={e => e.currentTarget.style.color = "#E2E8F0"}
                                >
                                  <X size={12} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add comment */}
                      <div style={{
                        padding: "10px 16px 14px", display: "flex", gap: 9, alignItems: "center",
                        borderTop: (feed.comments || []).length > 0 ? "1px solid #DBEAFE" : "none",
                        background: (feed.comments || []).length > 0 ? "#EFF6FF" : "white",
                      }}>
                        <UserAvatar name={currentName} size="sm" userProfiles={userProfiles} />
                        <input type="text" placeholder="Add a comment..."
                          value={commentInputs[feed.id] || ""}
                          onChange={e => setCommentInputs(p => ({ ...p, [feed.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === "Enter") handleComment(feed.id); }}
                          className="afm-mr-comment-input"
                          style={{
                            flex: 1, border: "1.5px solid #BFDBFE", borderRadius: 99,
                            padding: "7px 16px", fontSize: "0.8rem", fontFamily: "inherit",
                            background: "white", color: "#1E293B", outline: "none",
                            transition: "border-color 0.15s, box-shadow 0.15s",
                          }}
                        />
                        <button onClick={() => handleComment(feed.id)}
                          disabled={submittingComment[feed.id] || !commentInputs[feed.id]?.trim()}
                          style={{
                            width: 32, height: 32, borderRadius: "50%", border: "none",
                            background: "linear-gradient(135deg,#1E40AF,#2563EB)",
                            color: "white", cursor: "pointer", display: "flex",
                            alignItems: "center", justifyContent: "center",
                            boxShadow: "0 2px 8px rgba(37,99,235,0.3)",
                            opacity: (submittingComment[feed.id] || !commentInputs[feed.id]?.trim()) ? 0.4 : 1,
                            transition: "opacity 0.15s, transform 0.15s",
                          }}
                          onMouseEnter={e => { if (!submittingComment[feed.id] && commentInputs[feed.id]?.trim()) e.currentTarget.style.transform = "scale(1.1)"; }}
                          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                        >
                          <Send size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div ref={feedEndRef} />
              </div>
            </div>

            {/* RIGHT: Sidebar */}
            <div className="afm-mr-scrollbar" style={{
              width: 248, flexShrink: 0, borderLeft: "1px solid #BFDBFE",
              background: "#EFF6FF", overflowY: "auto", display: "flex", flexDirection: "column",
            }}>
              {/* MR card */}
              <div style={{
                margin: 14, borderRadius: 16,
                background: "linear-gradient(135deg,#1E40AF,#1D4ED8,#2563EB)",
                padding: 16, textAlign: "center",
                boxShadow: "0 8px 24px rgba(37,99,235,0.28)",
              }}>
                <div style={{ fontSize: "0.6rem", fontWeight: 700, letterSpacing: "0.1em",
                              textTransform: "uppercase", color: "rgba(255,255,255,0.65)", marginBottom: 4 }}>
                  Material Request
                </div>
                <div className="afm-mr-mono" style={{ fontWeight: 800, fontSize: "1.1rem",
                                                       color: "white", letterSpacing: "-0.01em" }}>
                  {mr.mrNo || mr.controlNo || `MR #${mrHeaderId}`}
                </div>
                {mr.mrDate && (
                  <div style={{ marginTop: 6, fontSize: "0.68rem", color: "rgba(255,255,255,0.6)" }}>
                    {formatDate(mr.mrDate)}
                  </div>
                )}
              </div>

              {/* Status */}
              <div style={{ margin: "-4px 14px 14px", display: "flex", justifyContent: "center" }}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: mrStatus.bg, color: mrStatus.color,
                  fontSize: "0.72rem", fontWeight: 700, padding: "5px 14px", borderRadius: 99,
                  border: `1px solid ${mrStatus.dot}44`,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: mrStatus.dot, flexShrink: 0 }} />
                  {mrStatus.label}
                </span>
              </div>

              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "0 14px 14px" }}>
                {[
                  { val: feeds.length,                                     label: "Posts",     emoji: "📝", color: "#1E40AF" },
                  { val: totalComments,                                    label: "Comments",  emoji: "💬", color: "#1D4ED8" },
                  { val: totalReactions,                                   label: "Reactions", emoji: "👍", color: "#2563EB" },
                  { val: feeds.filter(f => f.attachments?.length).length, label: "Files",     emoji: "📎", color: "#3B82F6" },
                ].map(s => (
                  <div key={s.label} style={{
                    background: "white", borderRadius: 12, padding: "10px 8px",
                    textAlign: "center", border: "1px solid #DBEAFE",
                    boxShadow: "0 1px 3px rgba(37,99,235,0.06)",
                  }}>
                    <div style={{ fontSize: "1rem", marginBottom: 2 }}>{s.emoji}</div>
                    <div style={{ fontSize: "1.15rem", fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</div>
                    <div style={{ fontSize: "0.62rem", fontWeight: 600, color: "#94A3B8", marginTop: 2 }}>{s.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ margin: "0 14px 14px", height: 1, background: "#BFDBFE" }} />

              {/* Details */}
              <div style={{ padding: "0 14px", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.08em",
                              textTransform: "uppercase", color: "#93C5FD", marginBottom: 2 }}>
                  MR Details
                </div>

                <InfoRow label="MR No."       value={mr.mrNo}                                        icon={Hash} />
                <InfoRow label="Control No."  value={mr.controlNo}                                   icon={Tag} />
                <InfoRow label="GI No."       value={mr.gI_Number || mr.gi_Number || mr.GI_Number}   icon={BarChart2} />
                <InfoRow label="A/R Invoice"  value={mr.arInvoiceNo}                                 icon={FileText} />
                <InfoRow label="Department"   value={mr.department || mr.departmentName}             icon={Building2} />
                <InfoRow label="Requested By" value={mr.requestedByName}                             icon={User} />
                <InfoRow label="Approved By"  value={mr.approvedByName}                              icon={CheckCircle} />
                <InfoRow label="Released By"  value={mr.releasedByName}                              icon={Package} />
                <InfoRow label="Date"         value={formatDate(mr.mrDate)}                          icon={Calendar} />
                {mr.branchSpecify && (
                  <InfoRow label="Branch"     value={mr.branchSpecify}                               icon={MapPin} />
                )}

                {mr.remarks && (
                  <div style={{ background: "#FFFBEB", border: "1px solid #FDE68A",
                                borderRadius: 12, padding: "10px 14px", marginTop: 2 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.65rem",
                                  fontWeight: 700, textTransform: "uppercase", color: "#92400E", marginBottom: 6 }}>
                      <StickyNote size={10} /> Remarks
                    </div>
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "#78350F", lineHeight: 1.6 }}>
                      {mr.remarks}
                    </p>
                  </div>
                )}

                {/* Items preview */}
                {(mr.mrDetails || []).length > 0 && (
                  <div style={{ background: "white", border: "1px solid #DBEAFE", borderRadius: 12,
                                padding: "10px 14px", marginTop: 2 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.65rem",
                                  fontWeight: 700, textTransform: "uppercase", color: "#93C5FD", marginBottom: 8 }}>
                      <Wrench size={10} /> Items ({mr.mrDetails.length})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {mr.mrDetails.slice(0, 4).map((item, idx) => (
                        <div key={idx} style={{
                          fontSize: "0.72rem", color: "#374151",
                          display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.4,
                        }}>
                          <span style={{ color: "#2563EB", fontWeight: 700, flexShrink: 0 }}>
                            {item.itemCode}
                          </span>
                          <span style={{ color: "#64748B", overflow: "hidden",
                                         textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.itemDescription}
                          </span>
                        </div>
                      ))}
                      {mr.mrDetails.length > 4 && (
                        <div style={{ fontSize: "0.7rem", color: "#60A5FA", fontWeight: 600 }}>
                          +{mr.mrDetails.length - 4} more items
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Allowed files */}
                <div style={{ background: "white", border: "1px solid #DBEAFE", borderRadius: 12,
                              padding: "10px 14px", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.65rem",
                                fontWeight: 700, textTransform: "uppercase", color: "#93C5FD", marginBottom: 8 }}>
                    <Paperclip size={10} /> Allowed Files
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {[
                      { label: "Images", icon: ImageIcon },
                      { label: "PDF",    icon: FileText },
                      { label: "DOCX",   icon: FileText },
                      { label: "XLSX",   icon: FileSpreadsheet },
                    ].map(({ label, icon: Icon }) => (
                      <span key={label} style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        background: "#DBEAFE", color: "#1E40AF", fontSize: "0.67rem",
                        fontWeight: 600, padding: "3px 8px", borderRadius: 99,
                        border: "1px solid #BFDBFE",
                      }}>
                        <Icon size={9} /> {label}
                      </span>
                    ))}
                  </div>
                  <p style={{ margin: "8px 0 0", fontSize: "0.68rem", color: "#94A3B8" }}>Max 10MB per file</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}